import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import {
  MessageCircle,
  HeartHandshake,
  Clock,
  ArrowRight,
  BookOpen,
  MessageSquare,
  Zap,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Helmet } from "react-helmet";
const CommunicationTechniques = () => {
  const [activeSection, setActiveSection] = useState("techniques");

  // Push ads to AdSense queue after component mounts
  useEffect(() => {
    document.title =
      "Effective Communication Techniques | Relationship Insights";
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, []);

  // Communication techniques data
  const techniques = [
    {
      id: 1,
      title: "Active Listening",
      description:
        "Active listening means fully concentrating on what is being said rather than passively hearing the message of the speaker.",
      tips: [
        "Give your full attention by putting away distractions",
        "Use appropriate body language (eye contact, nodding)",
        "Pause before responding to ensure you've processed what was said",
        'Paraphrase to confirm understanding: "What I hear you saying is..."',
      ],
      icon: <MessageCircle className="text-blue-500" size={24} />,
    },
    {
      id: 2,
      title: 'Use "I" Statements',
      description:
        '"I" statements focus on your feelings rather than placing blame, which helps reduce defensiveness in your partner.',
      tips: [
        'Structure as "I feel [emotion] when [situation] because [reason]"',
        'Avoid starting sentences with "You always" or "You never"',
        "Be specific about the situation rather than generalizing",
        "Focus on the current issue rather than bringing up past conflicts",
      ],
      icon: <MessageSquare className="text-emerald-500" size={24} />,
    },
    {
      id: 3,
      title: "Stay Calm During Conflicts",
      description:
        "Managing emotions prevents escalation and allows for productive discussion even during disagreements.",
      tips: [
        "Recognize your physical signs of anger or frustration",
        "Practice deep breathing if you feel emotions rising",
        "Take a time-out if needed, but commit to returning to the conversation",
        "Use a calm, even tone even when discussing difficult topics",
      ],
      icon: <HeartHandshake className="text-purple-500" size={24} />,
    },
    {
      id: 4,
      title: "Clarify and Ask Questions",
      description:
        "Seeking clarity ensures both parties are on the same page and prevents misunderstandings from snowballing.",
      tips: [
        "Ask open-ended questions that can't be answered with yes/no",
        'Use phrases like "Help me understand..." or "Could you elaborate on..."',
        "Avoid interrogating or questioning with an accusatory tone",
        "Be genuinely curious about your partner's perspective",
      ],
      icon: <Zap className="text-amber-500" size={24} />,
    },
    {
      id: 5,
      title: "Be Honest and Transparent",
      description:
        "Honesty builds trust and creates a foundation for authentic connection, even when truths are difficult.",
      tips: [
        "Share thoughts and feelings directly but with sensitivity",
        "Disclose information that impacts your relationship",
        "Express needs clearly rather than expecting your partner to read your mind",
        "Balance honesty with kindness in delivery",
      ],
      icon: <CheckCircle className="text-red-500" size={24} />,
    },
    {
      id: 6,
      title: "Practice Empathy",
      description:
        "Empathy means temporarily stepping into your partner's emotional experience to better understand their perspective.",
      tips: [
        "Listen for the feelings beneath your partner's words",
        "Validate emotions even if you don't agree with their perspective",
        "Ask yourself how you would feel in their position",
        "Show compassion through your words and actions",
      ],
      icon: <BookOpen className="text-indigo-500" size={24} />,
    },
    {
      id: 7,
      title: "Schedule Regular Check-Ins",
      description:
        "Dedicated communication time ensures important conversations happen before issues grow too large.",
      tips: [
        "Set a regular time that works for both partners",
        "Create a comfortable environment free from distractions",
        "Use a structured format with time for both partners to share",
        "Include positive discussions, not just problem-solving",
      ],
      icon: <Clock className="text-cyan-500" size={24} />,
    },
  ];

  // Communication barriers data
  const barriers = [
    {
      barrier: "Criticism",
      description:
        "Attacking your partner's character rather than addressing the behavior",
      example:
        '"You\'re so lazy" instead of "I feel frustrated when the chores aren\'t completed"',
      alternative:
        "Focus on the specific behavior and its impact on you using 'I' statements",
    },
    {
      barrier: "Defensiveness",
      description: "Responding to concerns with counter-complaints or excuses",
      example:
        '"Well, you do the same thing" or "It\'s not my fault because..."',
      alternative:
        "Take responsibility for your part and address their concern first before sharing yours",
    },
    {
      barrier: "Contempt",
      description:
        "Expressing superiority, disrespect, or disgust toward your partner",
      example: "Eye-rolling, mockery, hostile humor, or sarcasm",
      alternative:
        "Cultivate appreciation and respect, even during disagreements",
    },
    {
      barrier: "Stonewalling",
      description: "Withdrawing from the conversation or shutting down",
      example: "Silent treatment, leaving the room, or refusing to discuss",
      alternative:
        "If overwhelmed, ask for a specific time-out and set a time to resume the conversation",
    },
    {
      barrier: "Mind Reading",
      description:
        "Assuming you know what your partner is thinking without confirmation",
      example: '"I know you\'re just saying that to make me feel better"',
      alternative:
        "Ask clarifying questions and take statements at face value until proven otherwise",
    },
    {
      barrier: "All-or-Nothing Thinking",
      description: "Viewing situations in extreme, black-and-white terms",
      example:
        '"You never listen to me" or "You always prioritize work over me"',
      alternative:
        "Recognize the spectrum of behaviors and acknowledge the positive along with areas for improvement",
    },
  ];

  // Conversation starters data
  const conversationStarters = [
    {
      category: "Daily Connection",
      questions: [
        "What was the highlight of your day?",
        "Did anything happen today that made you think of me?",
        "What's something you're looking forward to this week?",
        "How can I support you right now?",
      ],
    },
    {
      category: "Deeper Understanding",
      questions: [
        "What's something you've been wanting to try but haven't yet?",
        "How do you prefer to receive love and support when you're stressed?",
        "What's a belief you had as a child that you've since changed?",
        "When do you feel most connected to me?",
      ],
    },
    {
      category: "Future Planning",
      questions: [
        "What's one thing you'd like us to experience together in the next year?",
        "How do you envision our relationship growing over time?",
        "What traditions would you like to create together?",
        "What goals would you like us to work toward as a couple?",
      ],
    },
    {
      category: "Navigating Differences",
      questions: [
        "When we disagree, what helps you feel heard and respected?",
        "How did your family handle conflict growing up, and how has that influenced you?",
        "What's one thing I could do differently when we have a disagreement?",
        "What's something you appreciate about how we resolve conflicts?",
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-indigo-50 text-slate-800">
      <Header />
      <Helmet>
        <title>Effective Communication Techniques</title>
        <meta
          name="description"
          content="Article talking about effective communication techniques with your partner."
        />
      </Helmet>
      {/* Top Banner Ad */}
      <div
        className="container mx-auto px-4 py-6"
        style={{ width: "100%", minWidth: "300px" }}>
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-2067522743161930"
          data-ad-slot="YYYYYYYYYY"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      </div>

      <main className="flex-grow container mx-auto px-4 py-12 max-w-5xl mt-16 relative">
        {/* Sidebar Ad (visible on medium screens and up) */}
        <div
          className="hidden md:block fixed right-4 top-24 w-48"
          style={{ width: "100%", minWidth: "300px" }}>
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="YYYYYYYYYY"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>

        {/* Hero Section */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-500">
            Effective Communication Techniques
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-slate-600">
            Communication is the lifeline of any relationship. Master these
            techniques to express yourself clearly, listen deeply, and build
            stronger connections with those you care about.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveSection("techniques")}
            className={`px-6 py-3 font-medium text-lg ${
              activeSection === "techniques"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            Core Techniques
          </button>
          <button
            onClick={() => setActiveSection("barriers")}
            className={`px-6 py-3 font-medium text-lg ${
              activeSection === "barriers"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            Common Barriers
          </button>
          <button
            onClick={() => setActiveSection("starters")}
            className={`px-6 py-3 font-medium text-lg ${
              activeSection === "starters"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            Conversation Starters
          </button>
        </div>

        {/* Core Techniques Content */}
        {activeSection === "techniques" && (
          <div>
            <p className="text-lg mb-8 text-slate-600">
              Mastering these fundamental communication skills can transform
              your relationships, helping you connect more deeply and resolve
              conflicts more effectively.
            </p>

            <div className="space-y-8">
              {techniques.map((technique, index) => (
                <React.Fragment key={technique.id}>
                  <div className="bg-white rounded-lg shadow-md p-6 transition-all hover:shadow-lg">
                    <div className="flex items-start gap-4">
                      <div className="mt-1 p-2 bg-slate-100 rounded-full">
                        {technique.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-slate-800 mb-2">
                          {technique.id}. {technique.title}
                        </h3>
                        <p className="text-slate-600 mb-4">
                          {technique.description}
                        </p>

                        <div className="bg-slate-50 rounded-md p-4">
                          <h4 className="font-medium text-blue-600 mb-2">
                            How to practice:
                          </h4>
                          <ul className="space-y-2">
                            {technique.tips.map((tip, tipIndex) => (
                              <li
                                key={tipIndex}
                                className="flex items-start gap-2">
                                <ArrowRight
                                  size={18}
                                  className="text-indigo-500 mt-1 flex-shrink-0"
                                />
                                <span className="text-slate-700">{tip}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* In-Content Ad after third technique */}
                  {index === 2 && (
                    <div
                      className="my-8"
                      style={{ width: "100%", minWidth: "300px" }}>
                      <ins
                        className="adsbygoogle"
                        style={{ display: "block" }}
                        data-ad-client="ca-pub-2067522743161930"
                        data-ad-slot="YYYYYYYYYY"
                        data-ad-format="auto"
                        data-full-width-responsive="true"></ins>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="mt-12 bg-blue-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">
                Communication Takes Practice
              </h3>
              <p className="text-slate-700">
                Remember that effective communication is a skill that develops
                over time. Be patient with yourself and your partner as you work
                on these techniques. Small, consistent improvements will lead to
                significant positive changes in how you connect with others.
              </p>
            </div>
          </div>
        )}

        {/* Communication Barriers Content */}
        {activeSection === "barriers" && (
          <div>
            <p className="text-lg mb-8 text-slate-600">
              Recognizing these common communication pitfalls is the first step
              to avoiding them. Learn to identify and address these barriers to
              foster more effective conversations.
            </p>

            <div className="space-y-6">
              {barriers.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <AlertCircle className="text-red-500 mr-3" size={22} />
                      <h3 className="text-xl font-semibold text-slate-800">
                        {item.barrier}
                      </h3>
                    </div>
                    <p className="text-slate-600 mb-4">{item.description}</p>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-red-50 p-4 rounded-md">
                        <h4 className="font-medium text-red-600 mb-2">
                          Example:
                        </h4>
                        <p className="text-slate-700 italic">{item.example}</p>
                      </div>

                      <div className="bg-green-50 p-4 rounded-md">
                        <h4 className="font-medium text-green-600 mb-2">
                          Better Approach:
                        </h4>
                        <p className="text-slate-700">{item.alternative}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-indigo-100 to-blue-100 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Breaking Patterns</h3>
              <p className="text-slate-700">
                Many of these communication barriers are learned patterns that
                can be difficult to break. If you notice yourself falling into
                these patterns, pause, acknowledge it, and try a different
                approach. With time and practice, healthier communication habits
                will become your new default.
              </p>
            </div>
          </div>
        )}

        {/* Conversation Starters Content */}
        {activeSection === "starters" && (
          <div>
            <p className="text-lg mb-8 text-slate-600">
              Sometimes the hardest part of communication is simply getting
              started. These thoughtful questions can help spark meaningful
              conversations and deepen your connection.
            </p>

            <div className="space-y-8">
              {conversationStarters.map((category, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">
                    {category.category}
                  </h3>

                  <div className="grid md:grid-cols-2 gap-4">
                    {category.questions.map((question, i) => (
                      <div
                        key={i}
                        className="bg-slate-50 p-4 rounded-md hover:bg-blue-50 transition-colors">
                        <p className="text-slate-700">"{question}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-slate-100 p-6 rounded-lg border-l-4 border-indigo-500">
              <h3 className="text-xl font-semibold mb-3 text-indigo-600">
                Creating a Safe Space
              </h3>
              <p className="text-slate-700">
                The best conversations happen when both people feel safe to be
                vulnerable. Set aside dedicated time free from distractions,
                approach with curiosity rather than judgment, and express
                appreciation for your partner's openness. Remember that the goal
                is connection, not necessarily agreement on every topic.
              </p>
            </div>
          </div>
        )}

        {/* Interactive Exercise */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold mb-4 text-center">
            Communication Practice Exercise
          </h3>
          <p className="text-slate-600 mb-6 text-center">
            Try this exercise with your partner to practice active listening and
            clear expression.
          </p>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg mb-6">
            <h4 className="font-semibold text-blue-700 mb-2">
              The Speaker-Listener Technique
            </h4>
            <ol className="space-y-3">
              <li className="flex items-start gap-2">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  1
                </span>
                <p className="text-slate-700">
                  Choose a topic that's meaningful but not highly charged
                  emotionally to start.
                </p>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  2
                </span>
                <p className="text-slate-700">
                  Designate one person as the speaker and one as the listener.
                </p>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  3
                </span>
                <p className="text-slate-700">
                  The speaker shares their thoughts and feelings using "I"
                  statements.
                </p>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  4
                </span>
                <p className="text-slate-700">
                  The listener paraphrases what they heard before responding.
                </p>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  5
                </span>
                <p className="text-slate-700">
                  Switch roles and repeat the process.
                </p>
              </li>
            </ol>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 border border-slate-200 rounded-md">
              <h5 className="font-medium text-indigo-600 mb-2">
                Speaker Guidelines:
              </h5>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle
                    size={18}
                    className="text-green-500 mt-1 flex-shrink-0"
                  />
                  <span className="text-slate-700">
                    Speak for yourself ("I feel..." not "You always...")
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle
                    size={18}
                    className="text-green-500 mt-1 flex-shrink-0"
                  />
                  <span className="text-slate-700">
                    Keep statements brief to allow for paraphrasing
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle
                    size={18}
                    className="text-green-500 mt-1 flex-shrink-0"
                  />
                  <span className="text-slate-700">
                    Stop and let listener reflect before continuing
                  </span>
                </li>
              </ul>
            </div>

            <div className="p-4 border border-slate-200 rounded-md">
              <h5 className="font-medium text-indigo-600 mb-2">
                Listener Guidelines:
              </h5>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle
                    size={18}
                    className="text-green-500 mt-1 flex-shrink-0"
                  />
                  <span className="text-slate-700">
                    Focus only on understanding, not forming responses
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle
                    size={18}
                    className="text-green-500 mt-1 flex-shrink-0"
                  />
                  <span className="text-slate-700">
                    Paraphrase: "What I hear you saying is..."
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle
                    size={18}
                    className="text-green-500 mt-1 flex-shrink-0"
                  />
                  <span className="text-slate-700">
                    Ask clarifying questions if needed
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Ad */}
        <div className="mt-12" style={{ width: "100%", minWidth: "300px" }}>
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="YYYYYYYYYY"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold mb-4">
            Commit to Better Communication
          </h3>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            Small improvements in how you communicate can lead to profound
            changes in your relationships. Choose one technique to focus on this
            week.
          </p>
          <div className="inline-block bg-gradient-to-r from-blue-600 to-indigo-500 p-px rounded-lg">
            <button className="bg-white hover:bg-slate-50 px-8 py-3 rounded-lg font-medium transition-colors">
              Download Communication Workbook
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CommunicationTechniques;
